import { 
  Sprout, 
  Microscope, 
  Leaf, 
  UserPlus 
} from "lucide-react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

interface FeatureProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  index: number;
}

const Feature = ({ icon, title, description, index }: FeatureProps) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
    rootMargin: "-50px 0px"
  });
  
  return (
    <motion.div 
      ref={ref}
      className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg text-center"
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
    >
      <motion.div 
        className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4"
        initial={{ scale: 0.8 }}
        animate={inView ? { scale: 1 } : { scale: 0.8 }}
        transition={{ duration: 0.3, delay: index * 0.1 + 0.2 }}
      >
        {icon}
      </motion.div>
      <h3 className="text-xl font-semibold mb-3 font-heading">{title}</h3>
      <p className="text-neutral-700">{description}</p>
    </motion.div>
  );
};

const FeaturesSection = () => {
  const [sectionRef, sectionInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const features = [
    {
      icon: <Sprout className="h-6 w-6 text-white" />,
      title: "Premium Quality Seeds",
      description: "Rigorously tested seeds with high germination rates and yield potential."
    },
    {
      icon: <Microscope className="h-6 w-6 text-white" />,
      title: "Advanced Research",
      description: "Continuous innovation through our state-of-the-art research facilities."
    },
    {
      icon: <Leaf className="h-6 w-6 text-white" />,
      title: "Sustainable Practices",
      description: "Environmentally conscious methods ensuring soil health and biodiversity."
    },
    {
      icon: <UserPlus className="h-6 w-6 text-white" />,
      title: "Farmer Support",
      description: "Comprehensive assistance from planting to harvest for optimal results."
    }
  ];

  const headerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section className="py-16 bg-background overflow-hidden">
      <div className="container mx-auto px-4">
        <motion.div 
          ref={sectionRef}
          className="text-center max-w-3xl mx-auto mb-16"
          initial="hidden"
          animate={sectionInView ? "visible" : "hidden"}
          variants={headerVariants}
        >
          <motion.span 
            className="text-primary font-medium inline-block"
            variants={itemVariants}
          >
            Why Choose Us
          </motion.span>
          <motion.h2 
            className="text-3xl md:text-4xl font-bold mt-2 mb-4 font-heading"
            variants={itemVariants}
          >
            Cultivating Excellence in Every Seed
          </motion.h2>
          <motion.p 
            className="text-neutral-700"
            variants={itemVariants}
          >
            At Varsha Seeds, we combine traditional wisdom with modern technology 
            to deliver seeds that ensure abundant harvests.
          </motion.p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <Feature 
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
